export const MAX_REQUEST_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 10_000;
const BASE_RETRY_DELAY_MS = 500;

// 429 means the server rejected the request before doing any work, so every method is safe to repeat.
// 503 may come after partial work, so only requests without side effects are repeated.
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);

export const isIdempotentMethod = (method: string | undefined): boolean =>
    IDEMPOTENT_METHODS.has((method ?? "get").toLowerCase());

export const shouldRetryRequest = (
    status: number | undefined,
    method: string | undefined,
    attempt: number,
): boolean => {
    if (attempt >= MAX_REQUEST_RETRIES) return false;
    if (status === 429) return true;
    if (status === 503) return isIdempotentMethod(method);
    return false;
};

// Retry-After is either delay-seconds or an HTTP date; without it back off exponentially with jitter
export const getRetryDelayMs = (
    retryAfter: string | null | undefined,
    attempt: number,
    now: number = Date.now(),
): number => {
    if (retryAfter) {
        const seconds = Number(retryAfter);
        if (Number.isFinite(seconds)) {
            return clampDelay(seconds * 1000);
        }
        const date = Date.parse(retryAfter);
        if (!Number.isNaN(date)) {
            return clampDelay(date - now);
        }
    }
    const backoff = BASE_RETRY_DELAY_MS * 2 ** attempt;
    return clampDelay(backoff + Math.random() * (backoff / 2));
};

const clampDelay = (ms: number) =>
    Math.min(Math.max(ms, 0), MAX_RETRY_DELAY_MS);
